import React, { useState, useRef, useEffect } from 'react';
import ReservationDashboardCard from './reservation-dashboard-card';
import {Clock, Check, XCircle, MoreVertical, CheckCircle} from 'lucide-react';
import { formatTimeInTimeZone } from '../../utils/timezone';
import {Appointment} from "../../model/professional/booking-model";
import { t } from 'i18next';



interface AppointmentSectionProps {
  title: string;
  appointments: Appointment[];
  count?: number;
  viewMode: 'card' | 'list';
  onStatusChange?: (id: string) => void;
}

// Composant pour afficher un rendez-vous en mode liste (cartes compactes)
const AppointmentListItem: React.FC<{ 
  appointment: Appointment;
  isOngoing: boolean;
  isPast: boolean;
  isNext: boolean;
  onStatusChange?: (id: string) => void;
}> = ({ appointment, isOngoing, isPast, isNext, onStatusChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Fermer le menu si on clique ailleurs sur la page
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleStatusChange = () => {
    if (onStatusChange) {
      onStatusChange(appointment.id);
    }
    setMenuOpen(false);
  };
  const now = new Date();
  // Forcer l'heure à 01:55 pour le test
  now.setHours(1, 55, 0, 0);
  
  const getAppointmentDate = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };
  
  // Formater l'heure pour un affichage plus élégant
  const formatTime = (timeStr: string) => {
    const date = getAppointmentDate(timeStr);
    return formatTimeInTimeZone(date, "HH'h'mm");
  };
  
  const appointmentTime = getAppointmentDate(appointment.time);
  const [durationMinutes] = appointment.duration.split(' ')[0].split(':').map(Number) || [0];
  const endTime = new Date(appointmentTime);
  endTime.setMinutes(endTime.getMinutes() + durationMinutes);


  const getCardStyle = () => {
    return 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750';
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'confirmed':
        return {
          icon: <Check size={12} />,
          color: 'bg-indigo-100 tex-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400',
          label: 'Confirmé'
        };
      case 'completed':
        return {
          icon: <CheckCircle size={12} />,
          color: 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400',
          label: 'Terminé'
        };
      case 'cancelled':
        return {
          icon: <XCircle size={12} />,
          color: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
          label: 'Annulé'
        };
      default:
        return {
          icon: null,
          color: 'bg-primary/10 text-primary dark:bg-primary-dark/20 dark:text-primary-light',
          label: status
        };
    }
  };

  const statusInfo = getStatusInfo(appointment.status);

  return (
    <div className="p-1 w-full">
      <div className={`relative rounded-lg border transition-all duration-300 p-3 h-full ${getCardStyle()} ${
        appointment.status === 'cancelled' || appointment.status === "completed" ? 'opacity-60' : ''
      }`}>
        <div className="flex justify-between items-start mb-2">
          <div className={`font-medium text-sm text-text-primary dark:text-white`}>
            {formatTime(appointment.time)}
          </div>
          {appointment.status !== "cancelled" && <div className="relative" ref={menuRef}>
            <button
                className="p-1 rounded-full hover:bg-background-dark/5 dark:hover:bg-gray-700 text-text-secondary dark:text-gray-400"
                onClick={() => setMenuOpen(!menuOpen)}
            >
              <MoreVertical size={14}/>
            </button>

            {menuOpen && (
                <div
                    className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    <button
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleStatusChange()}
                        role="menuitem"
                    >
                      <XCircle size={16} className="mr-2 text-red-500"/>
                      {t('dashboard.cancelAppointment', 'Marquer comme annulé')}
                    </button>
                  </div>
                </div>
            )}
          </div>}

        </div>

        <div className={`font-medium text-text-primary dark:text-white mb-1`}>
          {appointment.clientName}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          {appointment.service}
        </div>
        
        <div
          className={`flex items-center px-2 py-0.5 rounded-full text-xs ${statusInfo.color} mb-2 w-fit`}>
          {statusInfo.icon}
          <span className="ml-1">{statusInfo.label}</span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
            <Clock size={12} className="mr-1"/>
            {appointment.duration}
            <span className="ml-2 font-medium">{appointment.totalPrice} {t('dashboard.currency', '€')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReservationSection: React.FC<AppointmentSectionProps> = ({
                                                                 title,
                                                                 appointments,
                                                                 count,
                                                                 viewMode,
                                                                 onStatusChange
                                                               }) => {
  if (appointments.length === 0) return null;

  // Helper function to get appointment time as Date object
  const getAppointmentTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // Helper function to get appointment end time
  const getAppointmentEndTime = (appointment: Appointment) => {
    const startTime = getAppointmentTime(appointment.time);
    const [duration] = appointment.duration.split(' ')[0].split(':').map(Number);
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + duration);
    return endTime;
  };
  
  const currentTime = new Date();
  // Forcer l'heure à 01:55 pour le test
  currentTime.setHours(1, 55, 0, 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</h3>
        {count !== undefined && (
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {count} {t('dashboard.appointments', 'rendez-vous')}
          </span>
        )}
      </div>
      
      {viewMode === 'list' ? (
        // Affichage en mode carte (original)
        <div className="space-y-2">
          {appointments.map(appointment => (
            <ReservationDashboardCard
              key={appointment.id} 
              appointment={appointment} 
              onStatusChange={onStatusChange}
            />
          ))}
        </div>
      ) : (
        // Affichage en mode grille (comme dans l'image)
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {appointments.map(appointment => {
            const startTime = getAppointmentTime(appointment.time);
            const endTime = getAppointmentEndTime(appointment);
            const isOngoing = currentTime >= startTime && currentTime <= endTime && appointment.status !== 'cancelled';
            const isNext = !isOngoing && currentTime < startTime && appointment.status !== 'cancelled';
            const isPast = currentTime > endTime || appointment.status === 'cancelled';
            
            return (
              <AppointmentListItem 
                key={appointment.id} 
                appointment={appointment} 
                isOngoing={isOngoing}
                isPast={isPast}
                isNext={isNext}
                onStatusChange={onStatusChange}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReservationSection;